// <!-- GRACE:MODULE_START name="ui_asr" -->
// <!-- GRACE:DEPENDENCY target="ui_config" -->
// <!-- GRACE:DEPENDENCY target="ui_auth" -->
// <!-- GRACE:DEPENDENCY target="ui_types" -->
// <LLM_NOTE>
// При ошибке распознавания (processAudio, processAudioFile) хук возвращает MOCK-текст
// с hardcoded именем пациента на русском. Это fallback для разработки — в production
// пользователь видит ошибку + mock. Функция mock используется намеренно как graceful degradation.
//
// diarize + asyncMode → POST /api/speechkit/recognize/async (202) + polling GET /status/{id}
// diarize без asyncMode → синхронный POST /api/speechkit/recognize
// иначе — POST /api/speechkit/upload
// </LLM_NOTE>
import { useState, useRef, useCallback } from 'react';
import {
  AudioRecorderOptionsExtended,
  RecognitionOperationStatus,
  Segment,
} from '../types/asyncProcessing';
import API_ENDPOINTS from '../config/api';
import { getToken } from '../services/authService';

interface AudioRecorderReturn {
  isRecording: boolean;
  isSupported: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  audioLevel: number;
  audioBlob: Blob | null;
  audioUrl: string | null;
  processAudioFile: (file: File) => Promise<void>;
  resumePendingOperation: () => Promise<void>;
}

interface UploadJsonResult {
  status: number;
  body: Record<string, unknown>;
}

// <!-- GRACE:CONTRACT function="uploadFormDataXHR" -->
function uploadFormDataXHR(
  url: string,
  formData: FormData,
  token: string | null,
  onProgress: (percent: number) => void,
): Promise<UploadJsonResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      try {
        const body = JSON.parse(xhr.responseText) as Record<string, unknown>;
        resolve({ status: xhr.status, body });
      } catch {
        reject(new Error('Некорректный ответ сервера'));
      }
    };
    xhr.onerror = () => reject(new Error('Failed to fetch'));
    xhr.ontimeout = () => reject(new Error('Превышено время загрузки файла'));
    xhr.timeout = 0;
    xhr.send(formData);
  });
}

// <!-- GRACE:CONTRACT function="useAudioRecorder" -->
export default function useAudioRecorder({
  onTranscription,
  onProcessingStart,
  onProcessingEnd,
  onUploadStart,
  onUploadProgress,
  onOperationStart,
  onOperationProgress,
  onOperationError,
  diarize = false,
  asyncMode = diarize,
  pollingIntervalMs = 5000,
  suppressMockFallback = false,
  operationStorageKey,
  onSegments
}: AudioRecorderOptionsExtended): AudioRecorderReturn {
  const pollingRef = useRef(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isSupported, setIsSupported] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Проверка поддержки браузера
  const checkSupport = useCallback(() => {
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasMediaRecorder = !!window.MediaRecorder;
    const hasAudioContext = !!(window.AudioContext || (window as any).webkitAudioContext);

    setIsSupported(hasGetUserMedia && hasMediaRecorder && hasAudioContext);
    return hasGetUserMedia && hasMediaRecorder && hasAudioContext;
  }, []);

  // Мониторинг уровня звука для VAD
  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const updateLevel = () => {
      if (!analyserRef.current || !isRecording) return;

      analyserRef.current.getByteFrequencyData(dataArray);

      // Вычисляем средний уровень звука
      const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
      const normalizedLevel = average / 255;

      setAudioLevel(normalizedLevel);

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  }, [isRecording]);

  // Инициализация аудио контекста для VAD
  const initAudioContext = useCallback(async (stream: MediaStream) => {
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      audioContextRef.current = new AudioContext();

      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();

      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;

      source.connect(analyserRef.current);

      // Запуск мониторинга уровня звука
      monitorAudioLevel();
    } catch (error) {
      console.error('Ошибка инициализации AudioContext:', error);
    }
  }, [monitorAudioLevel]);

  const applyRecognitionResult = useCallback((
    result: { text?: string; segments?: Segment[] },
  ) => {
    if (diarize && Array.isArray(result.segments) && onSegments) {
      onSegments(result.segments);
    }
    if (result.text) {
      onTranscription(result.text);
      return;
    }
    throw new Error('No text returned from speech recognition service');
  }, [diarize, onSegments, onTranscription]);

  const pollOperationStatus = useCallback(async (
    operationId: string,
    intervalMs: number,
  ): Promise<RecognitionOperationStatus> => {
    const token = getToken();
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

    while (true) {
      const response = await fetch(API_ENDPOINTS.SPEECHKIT.OPERATION_STATUS(operationId), {
        headers,
      });
      if (!response.ok) {
        throw new Error(`Ошибка статуса операции: HTTP ${response.status}`);
      }
      const status: RecognitionOperationStatus = await response.json();
      if (onOperationProgress) {
        const remaining = Math.max(
          0,
          (status.estimated_seconds ?? 0) - (status.elapsed_seconds ?? 0),
        );
        onOperationProgress(status.progress ?? 0, remaining, status.stage);
      }
      if (status.status === 'completed') {
        return status;
      }
      if (status.status === 'error') {
        throw new Error(status.error || 'Ошибка распознавания речи');
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }, [onOperationProgress]);

  const clearStoredOperation = useCallback(() => {
    if (operationStorageKey) {
      sessionStorage.removeItem(operationStorageKey);
    }
  }, [operationStorageKey]);

  const storeOperation = useCallback((operationId: string) => {
    if (operationStorageKey) {
      sessionStorage.setItem(operationStorageKey, operationId);
    }
  }, [operationStorageKey]);

  const sendAudio = useCallback(async (blob: Blob, filename: string) => {
    if (pollingRef.current) {
      return;
    }
    pollingRef.current = true;
    onProcessingStart();
    onUploadStart();

    const useAsync = diarize && asyncMode;
    const endpoint = useAsync
      ? API_ENDPOINTS.SPEECHKIT.RECOGNIZE_ASYNC
      : diarize
        ? API_ENDPOINTS.SPEECHKIT.RECOGNIZE
        : `${API_ENDPOINTS.SPEECHKIT.UPLOAD}?language=ru-RU`;

    try {
      const formData = new FormData();
      formData.append('audio_file', blob, filename);
      if (diarize) {
        formData.append('language', 'ru-RU');
        formData.append('diarize', 'true');
      }

      const token = getToken();
      let uploadResult: Record<string, unknown>;

      if (useAsync) {
        const xhrResult = await uploadFormDataXHR(
          endpoint,
          formData,
          token,
          onUploadProgress,
        );
        if (xhrResult.status !== 202 && xhrResult.status !== 200) {
          throw new Error(`Upload error! status: ${xhrResult.status}`);
        }
        uploadResult = xhrResult.body;
      } else {
        onUploadProgress(25);
        await new Promise(resolve => setTimeout(resolve, 300));
        onUploadProgress(50);
        const uploadResponse = await fetch(endpoint, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        if (!uploadResponse.ok) {
          throw new Error(`Upload error! status: ${uploadResponse.status}`);
        }
        uploadResult = await uploadResponse.json();
        onUploadProgress(100);
      }

      const operationId = String(uploadResult.operation_id || `req-${Date.now()}`);
      onOperationStart(operationId);

      if (useAsync) {
        storeOperation(operationId);
        const pollMs = typeof uploadResult.polling_interval === 'number'
          ? uploadResult.polling_interval * 1000
          : pollingIntervalMs;
        const finalStatus = await pollOperationStatus(operationId, pollMs);
        clearStoredOperation();
        applyRecognitionResult({
          text: finalStatus.text,
          segments: finalStatus.segments,
        });
        return;
      }

      if (uploadResult.status === 'error') {
        const errorMsg = String(
          uploadResult.error || uploadResult.message || 'Recognition failed',
        );
        throw new Error(errorMsg);
      }

      applyRecognitionResult({
        text: typeof uploadResult.text === 'string' ? uploadResult.text : undefined,
        segments: Array.isArray(uploadResult.segments)
          ? (uploadResult.segments as Segment[])
          : undefined,
      });
    } catch (error) {
      console.error('Ошибка обработки аудио:', error);

      let errorMessage = 'Неизвестная ошибка';
      if (error instanceof Error) {
        errorMessage = error.message;
        if (error.message.includes('Failed to fetch')) {
          errorMessage = useAsync
            ? 'Ошибка связи с сервером при загрузке или опросе статуса распознавания'
            : `Ошибка подключения к серверу. Проверьте:\n1. Запущен ли backend сервер\n2. Правильность URL: ${endpoint}\n3. Сетевое подключение`;
        } else if (error.message.includes('NetworkError')) {
          errorMessage = 'Ошибка сети. Проверьте подключение к интернету';
        } else if (error.message.includes('CORS')) {
          errorMessage = 'Ошибка CORS. Проверьте настройки сервера';
        }
      }

      onOperationError(errorMessage);

      if (suppressMockFallback) {
        onTranscription(`Ошибка распознавания речи: ${errorMessage}`);
      } else {
        const mockText = "useAudioRecorder Fallback Пациент Иванов Сергей, сорок два года. Дата сегодня. Печень обычных размеров, эхогенность слегка повышена, очагов нет.";
        onTranscription(`Ошибка распознавания речи: ${errorMessage}\n\nMock результат:\n${mockText}`);
      }
    } finally {
      pollingRef.current = false;
      onProcessingEnd();
    }
  }, [
    diarize,
    asyncMode,
    pollingIntervalMs,
    suppressMockFallback,
    applyRecognitionResult,
    pollOperationStatus,
    storeOperation,
    clearStoredOperation,
    onTranscription,
    onProcessingStart,
    onProcessingEnd,
    onUploadStart,
    onUploadProgress,
    onOperationStart,
    onOperationError,
  ]);

  const resumePendingOperation = useCallback(async () => {
    if (!diarize || !asyncMode || !operationStorageKey || pollingRef.current) {
      return;
    }
    const operationId = sessionStorage.getItem(operationStorageKey);
    if (!operationId) {
      return;
    }

    pollingRef.current = true;
    onProcessingStart();
    onOperationStart(operationId);
    onUploadProgress(100);

    try {
      const finalStatus = await pollOperationStatus(operationId, pollingIntervalMs);
      clearStoredOperation();
      applyRecognitionResult({
        text: finalStatus.text,
        segments: finalStatus.segments,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      onOperationError(errorMessage);
      if (errorMessage.includes('404') || errorMessage.includes('не найден')) {
        clearStoredOperation();
      }
      onTranscription(`Ошибка распознавания речи: ${errorMessage}`);
    } finally {
      pollingRef.current = false;
      onProcessingEnd();
    }
  }, [
    diarize,
    asyncMode,
    operationStorageKey,
    pollingIntervalMs,
    applyRecognitionResult,
    pollOperationStatus,
    clearStoredOperation,
    onProcessingStart,
    onProcessingEnd,
    onUploadProgress,
    onOperationStart,
    onOperationError,
    onTranscription,
  ]);

  // Обработка записанного через микрофон аудио
  const processAudio = useCallback((blob: Blob) => {
    return sendAudio(blob, 'recording.webm');
  }, [sendAudio]);

  // Обработка загруженного аудио файла
  const processAudioFile = useCallback(async (file: File) => {
    const blob = new Blob([file], { type: file.type });
    setAudioBlob(blob);
    const url = URL.createObjectURL(blob);
    setAudioUrl(url);
    await sendAudio(blob, file.name);
  }, [sendAudio]);

  // Начало записи
  const startRecording = useCallback(async () => {
    if (!checkSupport()) {
      alert('Ваш браузер не поддерживает запись аудио');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // Для Yandex SpeechKit
          channelCount: 1
        }
      });

      // Инициализация аудио контекста
      await initAudioContext(stream);

      // Создание MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });

        // Сохранение аудио файла
        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // Остановка аудио контекста
        if (audioContextRef.current) {
          await audioContextRef.current.close();
          audioContextRef.current = null;
        }

        // Остановка анимации уровня звука
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        // Обработка аудио
        await processAudio(audioBlob);

        // Остановка всех треков
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(100); // Записываем кусками по 100мс
      setIsRecording(true);
      setAudioLevel(0);

    } catch (error) {
      console.error('Ошибка начала записи:', error);
      alert('Не удалось получить доступ к микрофону');
    }
  }, [checkSupport, initAudioContext, processAudio]);

  // Остановка записи
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  return {
    isRecording,
    isSupported,
    startRecording,
    stopRecording,
    audioLevel,
    audioBlob,
    audioUrl,
    processAudioFile,
    resumePendingOperation,
  };
}

// <!-- GRACE:MODULE_END name="ui_asr" -->
